import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import SEOHead from '@/components/common/SEOHead';
import WCSubmenu from '@/components/worldcup/WCSubmenu';
import { LoadingPage } from '@/components/common/Loading';
import { Calendar, MapPin, Filter, ChevronRight } from 'lucide-react';
import { translateCountryName, wcMatchSlug } from '@/lib/wcTranslations';

const API_BASE = '';
const LOCALE_MAP = { tr: 'tr-TR', en: 'en-US', es: 'es-ES', de: 'de-DE', fr: 'fr-FR', pt: 'pt-BR', it: 'it-IT', nl: 'nl-NL', ru: 'ru-RU', uk: 'uk-UA', ar: 'ar-SA', id: 'id-ID' };

export default function WorldCupScores() {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roundFilter, setRoundFilter] = useState('');
  const wcBase = currentLang === 'tr' ? `/${currentLang}/dunya-kupasi-2026` : `/${currentLang}/world-cup-2026`;

  useEffect(() => {
    const params = new URLSearchParams({ lang: currentLang });
    if (roundFilter) params.set('round', roundFilter);
    fetch(`${API_BASE}/api/worldcup/fixtures?${params}`)
      .then(r => r.json()).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, [currentLang, roundFilter]);

  if (loading) return <LoadingPage />;
  if (!data) return null;

  const { fixtures, rounds } = data;
  const matchPath = currentLang === 'tr' ? 'mac' : 'match';

  const byDate = {};
  fixtures.forEach(f => {
    const d = f.date?.slice(0, 10) || 'TBD';
    if (!byDate[d]) byDate[d] = [];
    byDate[d].push(f);
  });

  const localRound = (r) => r.replace('Group Stage - ', t('wc.groupStagePrefix'));

  return (
    <div data-testid="wc-scores-page">
      <WCSubmenu />
      <SEOHead title={t('wc.scoresFixtures') + ' - ' + t('wc.breadcrumb') + ' | ODDSIS'}
        canonical={`https://oddsis.com/${currentLang}/${currentLang === 'tr' ? 'dunya-kupasi-2026/skor-ve-fikstur' : 'world-cup-2026/scores'}`} />

      {/* Hero */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #020617 0%, #0a1628 40%, #1a2744 100%)' }}>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `url(https://images.pexels.com/photos/5733516/pexels-photo-5733516.jpeg?auto=compress&cs=tinysrgb&w=1200)`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="relative z-10 container mx-auto px-4 max-w-5xl py-8">
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-4">
            <Link to={`/${currentLang}`} className="hover:text-slate-200">{t('nav.home')}</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={wcBase} className="hover:text-amber-300 text-amber-400/70">{t('wc.breadcrumb')}</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white">{t('wc.scoresFixtures')}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Calendar className="w-8 h-8 inline mr-3 text-amber-400" />
            {t('wc.scoresTitle')}
          </h1>
          <p className="text-sm text-slate-400">{fixtures.length} {t('wc.matchesLabel')} - FIFA {t('wc.breadcrumb').replace('2026','').trim()} 2026</p>
        </div>
      </div>

      <div className="container mx-auto px-4 max-w-5xl py-6">
        {/* Round Filter */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
          <Filter className="w-4 h-4 text-slate-500 shrink-0" />
          <button onClick={() => setRoundFilter('')} data-testid="round-filter-all"
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${!roundFilter ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-slate-800/40 text-slate-500 hover:text-slate-300 border border-transparent'}`}>
            {t('wc.allFilter')}
          </button>
          {rounds.map(r => (
            <button key={r} onClick={() => setRoundFilter(r)} data-testid={`round-filter-${r}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${roundFilter === r ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-slate-800/40 text-slate-500 hover:text-slate-300 border border-transparent'}`}>
              {localRound(r)}
            </button>
          ))}
        </div>

        {/* Matches by Date */}
        {Object.entries(byDate).map(([date, matches]) => (
          <div key={date} className="mb-8">
            <div className="flex items-center gap-3 mb-3 px-1">
              <div className="w-1 h-8 bg-amber-500 rounded-full" />
              <div>
                <h3 className="text-base font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {new Date(date).toLocaleDateString(LOCALE_MAP[currentLang] || 'en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </h3>
                <span className="text-[10px] text-slate-500">{matches.length} {t('wc.matchesLabel')}</span>
              </div>
            </div>
            <div className="space-y-1.5">
              {matches.map(m => {
                const time = m.date ? new Date(m.date).toLocaleTimeString(LOCALE_MAP[currentLang] || 'en-US', { hour: '2-digit', minute: '2-digit' }) : '';
                const isTR = m.home_name === 'Türkiye' || m.away_name === 'Türkiye';
                const isFinished = m.status === 'Match Finished';
                return (
                  <Link key={m.id} to={`${wcBase}/${matchPath}/${wcMatchSlug(m.home_name, m.away_name, m.id, currentLang)}`} data-testid={`fixture-${m.id}`}
                    className={`group flex items-center gap-3 px-4 py-3 rounded-xl border transition-all hover:bg-slate-800/50 hover:border-amber-500/20 ${
                      isTR ? 'border-red-500/20 bg-red-500/5' : 'border-slate-800/50 bg-slate-900/30'
                    }`}>
                    {/* Time */}
                    <div className="w-14 text-center shrink-0">
                      {isFinished ? (
                        <span className="text-xs font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">FT</span>
                      ) : (
                        <span className="text-sm font-mono text-slate-400 tabular-nums">{time}</span>
                      )}
                    </div>

                    {/* Home */}
                    <div className="flex items-center gap-2.5 flex-1 justify-end">
                      <span className={`text-sm font-semibold text-right ${m.home_name === 'Türkiye' ? 'text-red-400' : 'text-slate-200'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{translateCountryName(m.home_name, currentLang)}</span>
                      <img src={m.home_logo} alt="" className="w-7 h-7 rounded-sm" />
                    </div>

                    {/* Score / VS */}
                    <div className="w-20 text-center shrink-0">
                      {m.score_home != null ? (
                        <span className="text-lg font-black text-amber-400 tabular-nums bg-slate-950 px-3 py-1 rounded-lg border border-slate-800">{m.score_home} - {m.score_away}</span>
                      ) : (
                        <span className="text-xs text-slate-600 font-bold">VS</span>
                      )}
                    </div>

                    {/* Away */}
                    <div className="flex items-center gap-2.5 flex-1">
                      <img src={m.away_logo} alt="" className="w-7 h-7 rounded-sm" />
                      <span className={`text-sm font-semibold ${m.away_name === 'Türkiye' ? 'text-red-400' : 'text-slate-200'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{translateCountryName(m.away_name, currentLang)}</span>
                    </div>

                    {/* Meta */}
                    <div className="hidden md:flex items-center gap-3 w-48 shrink-0 justify-end">
                      <span className="text-[10px] text-amber-400/60 font-medium">{localRound(m.round)}</span>
                      <span className="text-[10px] text-slate-600 flex items-center gap-1"><MapPin className="w-2.5 h-2.5" />{m.venue_name}</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-700 group-hover:text-amber-400 transition-colors shrink-0" />
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
